/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as RadixUiDropdownMenu from "@radix-ui/react-dropdown-menu";
import type { ComponentProps, JSX } from "react";
import { forwardRef } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  numericPixels,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { Text } from "../../_core/Text/Text.emotion";
import { CheckmarkIcon } from "../../icons/Icon/GeneralIcons";
import type { IconElement } from "../../icons/Icon/Icon";

const baseStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: colors.transparent,
  borderRadius: radius.m,
  color: theme.semanticColors.primary,
  columnGap: spacing.m,
  cursor: "pointer",
  display: "inline-flex",
  height: "auto",
  maxWidth: spacing.full,
  minHeight: numericPixels[56],
  minWidth: numericPixels[300],
  outline: "none",
  overflow: "hidden",
  padding: spacing.m,
  width: spacing.full,
  ":hover": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.primary,
  },
  ":focus": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.primary,
  },
  ":active": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.neutralsMedHigh,
  },
});
const selectedStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
});
const checkIconStyle: Interpolation = {
  lineHeight: 0,
  marginLeft: "auto",
};
const startIconStyle: Interpolation = {
  lineHeight: 0,
  marginRight: spacing.s,
};

export type DropdownMenuItemProps = ComponentProps<
  typeof RadixUiDropdownMenu.Item
> & {
  selected?: boolean;
  startIcon?: IconElement;
  hideCheckOnSelected?: boolean;
};

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  DropdownMenuItemProps
>(
  (
    {
      onSelect,
      children,
      selected = false,
      startIcon,
      hideCheckOnSelected,
      ...props
    },
    ref,
  ) => {
    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        selected && !hideCheckOnSelected ? (
          <span key="checkmarkSimpleOn" css={checkIconStyle}>
            <CheckmarkIcon.SimpleOn />
          </span>
        ) : null,
      ],
      beforeChildrenInject: [
        startIcon ? (
          <span key="startIcon" css={startIconStyle}>
            {startIcon}
          </span>
        ) : null,
      ],
      children: children as JSX.Element,
    });

    return (
      <Text
        key={`dropdown-menu-item-${children}`}
        asChild
        size="m"
        type="title"
      >
        <RadixUiDropdownMenu.Item
          key={`dropdown-menu-item-${children}`}
          ref={ref}
          onSelect={onSelect}
          css={[baseStyle, selected && selectedStyle]}
          {...props}
        >
          {DeterminedChildren}
        </RadixUiDropdownMenu.Item>
      </Text>
    );
  },
);

DropdownMenuItem.displayName = "DropdownMenuItem";
